/**
 * What "Same as billing address" does to the shipping fields, shared by the three forms that have
 * the checkbox (Profile drawer, checkout review page, Edit Billing Details dialog) so they behave
 * identically.
 *
 *   Ticking   — the shipping fields' current values are remembered, then each form's own sync copies
 *               billing into them (that copy is what gets saved while ticked, as before).
 *   Unticking — the remembered values come back: the address the user had typed, or the different
 *               shipping address saved before. When nothing was remembered (the form loaded already
 *               ticked, or shipping was empty), the fields come back EMPTY, never as a billing copy.
 *
 * A billing copy on untick looked finished, so a user could save a "different" shipping address that
 * was really billing; and ticking used to throw away an address the user had typed.
 */

/** The form fields that make up a shipping address (country is shared with billing). */
export const SHIPPING_ADDRESS_FIELDS = [
  'shippingAddress1',
  'shippingAddress2',
  'shippingAddress3',
  'shippingPostalCode',
  'shippingRegion',
];

/**
 * Reads the shipping fields to remember, plus the shared country they belong to.
 * `getValue(name)` returns a field's current value.
 */
export function snapshotShippingAddress(getValue) {
  return {
    ...Object.fromEntries(SHIPPING_ADDRESS_FIELDS.map((name) => [name, getValue(name) ?? ''])),
    country: getValue('country') ?? '',
  };
}

/**
 * Writes the remembered shipping fields back, or empties them when there is nothing to restore.
 * `setValue` is react-hook-form's; `currentCountry` is the shared country now.
 *
 * - The remembered Region/State is dropped when the country has changed since ticking: it is a
 *   region of the old country, which the new country's list does not contain.
 * - Empty fields are never validated, so unticking after a failed save doesn't flag them before the
 *   user has typed anything. Restored, filled fields are validated, so an old error (left on the
 *   hidden billing copy) does not stay under them.
 */
export function restoreShippingAddress(setValue, snapshot, currentCountry) {
  const countryChanged = snapshot != null && (snapshot.country ?? '') !== (currentCountry ?? '');
  SHIPPING_ADDRESS_FIELDS.forEach((name) => {
    const value = name === 'shippingRegion' && countryChanged ? '' : (snapshot?.[name] ?? '');
    setValue(name, value, { shouldDirty: true, shouldValidate: value !== '' });
  });
}
